import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react"
import { Check, ChevronDown } from "lucide-react"

export type CallSelectOption = { value: string; label: string }

type CallSelectProps = {
  id: string
  /** Visible label above the control, or the accessible name only when `hideLabel` is set. */
  label: string
  hideLabel?: boolean
  value: string
  options: CallSelectOption[]
  onChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
  icon?: ReactNode
  /** Open the list above the control (menus that sit at the bottom of the stage). */
  openUp?: boolean
  size?: "sm" | "md"
  className?: string
}

/**
 * A dark, styled dropdown for the call (device pickers, preferences, breakout moves) in place of
 * the browser's native select, which renders as the operating system's own control. It is a
 * combobox button with a listbox: click or Space/Enter opens it, arrows move, Enter picks,
 * Escape or a click elsewhere closes it.
 */
export function CallSelect({ id, label, hideLabel = false, value, options, onChange, disabled = false, placeholder = "Choose", icon, openUp = false, size = "md", className = "" }: CallSelectProps) {
  const [open, setOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(0)
  const [placement, setPlacement] = useState({ above: openUp, maxHeight: 224 })
  const rootRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const listRef = useRef<HTMLUListElement | null>(null)
  const listId = `${id}-listbox`
  const labelId = `${useId()}-label`
  const selectedIndex = options.findIndex((option) => option.value === value)
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null

  useLayoutEffect(() => {
    if (!open) return
    const updatePlacement = () => {
      const root = rootRef.current
      if (!root) return
      const bounds = root.getBoundingClientRect()
      let top = 8
      let bottom = window.innerHeight - 8
      // Respect both the window and a scrollable Settings pane. This lets long,
      // wrapped device names stay reachable in short windows without clipping.
      for (let parent = root.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
        if (!/(auto|scroll|hidden|clip)/.test(window.getComputedStyle(parent).overflowY)) continue
        const parentBounds = parent.getBoundingClientRect()
        top = Math.max(top, parentBounds.top + 4)
        bottom = Math.min(bottom, parentBounds.bottom - 4)
      }
      const above = Math.max(0, bounds.top - top - 4)
      const below = Math.max(0, bottom - bounds.bottom - 4)
      const preferredSpace = openUp ? above : below
      const alternateSpace = openUp ? below : above
      const placeAbove = preferredSpace >= 224 || preferredSpace >= alternateSpace ? openUp : !openUp
      const maxHeight = Math.min(224, placeAbove ? above : below)
      setPlacement((current) => current.above === placeAbove && current.maxHeight === maxHeight ? current : { above: placeAbove, maxHeight })
    }
    updatePlacement()
    window.addEventListener("resize", updatePlacement)
    document.addEventListener("scroll", updatePlacement, true)
    return () => {
      window.removeEventListener("resize", updatePlacement)
      document.removeEventListener("scroll", updatePlacement, true)
    }
  }, [open, openUp])

  useEffect(() => {
    if (!open) return
    setHighlighted(selectedIndex >= 0 ? selectedIndex : 0)
    const handlePointer = (event: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handlePointer)
    document.addEventListener("touchstart", handlePointer)
    return () => {
      document.removeEventListener("mousedown", handlePointer)
      document.removeEventListener("touchstart", handlePointer)
    }
  }, [open, selectedIndex])

  useEffect(() => {
    const list = listRef.current
    const option = list?.children[highlighted] as HTMLElement | undefined
    if (!open || !list || !option) return
    if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop
    else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight
    }
  }, [open, highlighted])

  const pick = (option: CallSelectOption) => {
    setOpen(false)
    buttonRef.current?.focus()
    // Re-picking the current device is a valid request (Daily re-applies it), so always report the pick.
    onChange(option.value)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      const delta = event.key === "ArrowDown" ? 1 : -1
      setHighlighted((index) => (index + delta + options.length) % options.length)
      return
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      const option = options[highlighted]
      if (option) pick(option)
      return
    }
    if (event.key === "Escape" && open) {
      event.preventDefault()
      setOpen(false)
    }
  }

  const height = size === "sm" ? "min-h-9 py-1.5 text-xs" : "min-h-11 py-2 text-sm"

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      {hideLabel ? (
        <span id={labelId} className="sr-only">{label}</span>
      ) : (
        <span id={labelId} className="mb-1 block text-xs font-semibold uppercase tracking-wide text-white/60">{label}</span>
      )}
      <button
        ref={buttonRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? `${listId}-option-${highlighted}` : undefined}
        aria-labelledby={labelId}
        disabled={disabled || options.length === 0}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={handleKeyDown}
        className={`flex w-full min-w-0 items-center gap-2 rounded-lg border border-white/15 bg-[#1c2530] px-3 text-left text-white transition-colors hover:border-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f6a35c] disabled:cursor-not-allowed disabled:opacity-50 ${height}`}
      >
        {icon && <span className="shrink-0 text-white/60">{icon}</span>}
        <span className={`min-w-0 flex-1 whitespace-normal break-words leading-5 ${selected ? "" : "text-white/50"}`}>{selected ? selected.label : placeholder}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-white/60 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-labelledby={labelId}
          style={{ maxHeight: placement.maxHeight }}
          className={`absolute left-0 right-0 z-[70] overflow-y-auto rounded-lg border border-white/10 bg-[#0f1419] p-1 text-sm text-white shadow-2xl ${placement.above ? "bottom-full mb-1" : "top-full mt-1"}`}
        >
          {options.map((option, index) => {
            const isSelected = option.value === value
            return (
              <li
                key={option.value || `option-${index}`}
                id={`${listId}-option-${index}`}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setHighlighted(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(option)}
                className={`flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 ${index === highlighted ? "bg-white/10" : ""}`}
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">{isSelected && <Check className="h-4 w-4 text-[#f6a35c]" aria-hidden="true" />}</span>
                <span className="min-w-0 flex-1 whitespace-normal break-words leading-5">{option.label}</span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
